import { Doc } from '@dadohr/shared/domainTypes/document';
import { GREENHOUSE_V3_FIELDS } from '@dadohr/shared/domainTypes/integrations/greenhouseV3';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import {
  DefaultDadoField,
  UserImportDataSourceGreenhouseV3,
  UserImportDataSourceStatus,
  UserImportMapping,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { Button, DialogActions, DialogContent } from '@material-ui/core';
import { useState } from 'react';
import shortid from 'shortid';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { Loader } from '../../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../../components/SomethingWentWrong';
import { usePromise } from '../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { setDoc } from '../../../../../../services/firebase';
import { now } from '../../../../../../services/time';
import { useUserFields } from '../../../../../../services/user';
import {
  getGreenhouseV3Fields,
  useGreenhouseV3Connected
} from '../../../../../Org/pages/Integrations/integrations/greenhouseV3/service';
import { CustomImportMappingFormWithAutocomplete } from '../CustomImportMappingForm';
import { Img, ImportSourceListItem, IntegrationNotConfigured } from './common';
import { IMPORT_META } from './meta';

const META = IMPORT_META.GREENHOUSE_V3;

// v3 (Harvest v3, OAuth) exposes different field ids than v1 (offers use
// `starts_on`, custom fields live under `custom_fields`), so the default
// mapping is built against the v3 field ids. Sources are created manually on
// v3 migration — existing v1 GREENHOUSE sources are NOT converted.
const DEFAULT_MAPPINGS: UserImportMapping[] = [
  {
    dadoKey: DefaultDadoField.PREFERRED_NAME,
    otherKey: GREENHOUSE_V3_FIELDS.FIRST_NAME
  },
  {
    dadoKey: DefaultDadoField.LAST_NAME,
    otherKey: GREENHOUSE_V3_FIELDS.LAST_NAME
  },
  {
    dadoKey: DefaultDadoField.PERSONAL_EMAIL,
    otherKey: GREENHOUSE_V3_FIELDS.PERSONAL_EMAIL
  },
  {
    dadoKey: DefaultDadoField.HIRE_DATE,
    otherKey: GREENHOUSE_V3_FIELDS.FDAW
  }
];

export const GreenhouseV3SetupFlow = ({
  orgId,
  onClose,
  customFields
}: {
  orgId: string;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { id: userId } = useCurrentUser();
  // v3 connection status comes from the configInfos.greenhouseV3 projection.
  const [connected, loading, error] = useGreenhouseV3Connected(orgId);
  const { t } = useUnsafeTranslation();
  const userFields = useUserFields(customFields);

  const [mappings, setMappings] = useState<UserImportMapping[]>(
    DEFAULT_MAPPINGS
  );

  const [greenhouseFields, loadingFields, errorFields] = usePromise(
    () => getGreenhouseV3Fields(orgId),
    [orgId]
  );

  if (loading || loadingFields) {
    return (
      <DialogContent>
        <Loader height={250} />
      </DialogContent>
    );
  }
  if (error || errorFields) {
    return <SomethingWentWrong height={150} />;
  }

  if (!connected) {
    return (
      <IntegrationNotConfigured integration={META.name} onClose={onClose} />
    );
  }

  return (
    <>
      <DialogContent>
        <FormRow>
          <Img maxHeight={32} src={META.logoUrl} />
        </FormRow>
        {greenhouseFields && (
          <CustomImportMappingFormWithAutocomplete
            userFields={userFields}
            integrationName={META.name}
            name={t('Default Field Mapping')}
            mappings={mappings}
            onChange={(mappings) => setMappings(mappings)}
            fields={greenhouseFields}
            additionalCompositeFields={['$FULL_NAME$']}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {t('Cancel')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={async () => {
            const nextSource: Doc<UserImportDataSourceGreenhouseV3> = {
              id: shortid(),
              collection: FS.userImportDataSources,
              data: {
                orgId: orgId,
                type: UserImportSourceType.GREENHOUSE_V3,
                status: UserImportDataSourceStatus.PENDING,
                detailedStatus: '',
                lastUpdate: null,
                createdBy: userId,
                createdAt: now(),
                customMappings: mappings.filter((m) => !!m.otherKey),
                offerStatusToFilterBy: 'Accepted'
              }
            };
            await setDoc(nextSource);
            onClose();
          }}
        >
          {t('Add source')}
        </Button>
      </DialogActions>
    </>
  );
};

export const ImportSourceRowGreenhouseV3 = ({
  d
}: {
  d: Doc<UserImportDataSourceGreenhouseV3>;
}) => {
  return (
    <ImportSourceListItem
      enabled={d.data.status === UserImportDataSourceStatus.OK}
      name={META.name}
      logoUrl={META.smallLogoUrl}
    />
  );
};
